import { Injectable } from '@angular/core';
import { TAccordionMode } from '@shared/v2';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map, shareReplay } from 'rxjs';

@Injectable()
export class AccordionService {
  private mode: TAccordionMode = 'multiple';
  private openSet = new Set<string>();
  private openSet$ = new BehaviorSubject<ReadonlySet<string>>(new Set());

  activeItemIds$ = this.openSet$.pipe(
    map((set) => [...set]),
    distinctUntilChanged((prev, curr) => {
      if (prev.length !== curr.length) {
        return false;
      }

      return prev.every((id, index) => id === curr[index]);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  setMode(mode: TAccordionMode): void {
    this.mode = mode;
    if (mode === 'single' && this.openSet.size > 1) {
      const first = this.openSet.values().next().value as string | undefined;
      this.openSet.clear();
      if (first) this.openSet.add(first);
      this.emit();
    }
  }

  toggle(id: string): void {
    if (this.openSet.has(id)) {
      this.openSet.delete(id);
      this.emit();
    } else {
      this.open(id);
    }
  }

  open(id: string): void {
    if (this.mode === 'single') {
      if (this.openSet.size !== 1 || !this.openSet.has(id)) {
        this.openSet.clear();
        this.openSet.add(id);
        this.emit();
      }
    } else {
      if (!this.openSet.has(id)) {
        this.openSet.add(id);
        this.emit();
      }
    }
  }

  close(id: string): void {
    if (this.openSet.delete(id)) {
      this.emit();
    }
  }

  isOpen(id: string): boolean {
    return this.openSet.has(id);
  }

  watch(id: string): Observable<boolean> {
    return this.openSet$.pipe(
      map((set) => set.has(id)),
      distinctUntilChanged(),
    );
  }

  watchMultiple(ids: string[]): Observable<{ [key: string]: boolean }> {
    return combineLatest(ids.map((id) => this.watch(id))).pipe(
      map((statuses) => {
        const result: { [key: string]: boolean } = {};
        ids.forEach((id, index) => {
          result[id] = statuses[index];
        });

        return result;
      }),
      distinctUntilChanged((prev, curr) => {
        return ids.every((id) => prev[id] === curr[id]);
      }),
    );
  }

  private emit(): void {
    this.openSet$.next(new Set(this.openSet));
  }
}
